import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, Subscription, catchError, map, throwError } from 'rxjs';

import type { User } from '../../../shared/models/user.model';
import { ApiService } from '../../../shared/services/api.service';
import { AuthService } from '../../../shared/services/auth.service';

/** Cuerpo de `PUT /profile`: solo estas tres claves (la API lee con `only`). `phone` vacío va como `null`. */
export interface ProfilePersonalData {
  name: string;
  surnames: string;
  phone: string | null;
}

/** 200 de `PUT /profile` (A3, `docs/api/profile-api.md` §A3). */
export interface ProfileUpdateResult {
  data: ProfilePersonalData;
  message: string | null;
}

/** Lo que la web lee del 200 de `PUT /users/{id}`: el user entero llega SIN sobre `data` (`showOne`). */
export interface EmailChangeResult {
  email: string;
  verified: boolean;
}

/**
 * Fallo tipado de los ajustes. `validation` es todo 422, con el primer texto del servidor por campo
 * (`{error: {campo: [...]}}`; `{}` si no trae campos);
 * `unauthorized` ya ha lanzado `auth.me()`: la sesión se limpia y el contenedor vuelve a «Inicia sesión…».
 */
export type ProfileSettingsFailure =
  | { kind: 'validation'; fields: Record<string, string> }
  | { kind: 'forbidden'; message: string }
  | { kind: 'unauthorized' }
  | { kind: 'error' };

/**
 * `PUT /profile` (A3) y `PUT /users/{id}` con `{email}` (`UserController::update`, A3-bis) para `/perfil/ajustes`
 * (W3, §5.6.5 de B10). Mismo patrón que `ProfileOrdersService` (W1):
 *
 * - **Solo en navegador** (§5.6.6): en SSR no hay petición; los métodos completan sin emitir.
 * - **401 → `auth.me()`** (§5.6.7), una sola recuperación en vuelo. Nunca `logout()`, que navega a `/`.
 * - **Sin estado**: lo que se pinta lo guarda el componente atado a `currentUser()?.id`.
 */
@Injectable({ providedIn: 'root' })
export class ProfileSettingsService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  /** Recuperación de sesión tras un 401: una sola en vuelo aunque fallen a la vez datos y correo. */
  private recovery: Subscription | null = null;

  updateProfile(data: ProfilePersonalData): Observable<ProfileUpdateResult> {
    if (!this.isBrowser) return EMPTY;
    const body: ProfilePersonalData = { name: data.name, surnames: data.surnames, phone: data.phone };
    return this.api.put<{ data: ProfilePersonalData; message?: unknown }>('/profile', body).pipe(
      map((res) => ({
        data: res.data,
        message: typeof res.message === 'string' && res.message ? res.message : null,
      })),
      catchError((err: unknown) => this.fail(err)),
    );
  }

  /**
   * Solo `{email}`. El componente ya ha validado el formato y que cambia: `email` no es `required` en la API y un
   * `""` acaba en 500, y un `PUT` del correo actual de una cuenta sin verificar reenvía el correo de verificación.
   */
  changeEmail(userId: number, email: string): Observable<EmailChangeResult> {
    if (!this.isBrowser) return EMPTY;
    return this.api.put<User>(`/users/${userId}`, { email }).pipe(
      map((user) => ({ email: user.email, verified: user.verified === true })),
      catchError((err: unknown) => this.fail(err)),
    );
  }

  private fail(err: unknown): Observable<never> {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const body = err instanceof HttpErrorResponse ? (err.error as { error?: unknown } | null) : null;

    if (status === 401) {
      this.recoverSession();
      return throwError((): ProfileSettingsFailure => ({ kind: 'unauthorized' }));
    }

    if (status === 403) {
      const message = typeof body?.error === 'string' ? body.error : '';
      return throwError((): ProfileSettingsFailure => ({ kind: 'forbidden', message }));
    }

    if (status === 422) {
      // Sigue siendo un 422 aunque no traiga campos (`fields: {}`): el correo lo pinta como EMAIL_INVALID y los datos
      // personales, como SAVE_ERROR (H3 de la Astra p1).
      const fields = firstMessages(body?.error);
      return throwError((): ProfileSettingsFailure => ({ kind: 'validation', fields }));
    }

    return throwError((): ProfileSettingsFailure => ({ kind: 'error' }));
  }

  /** Copia de `ProfileOrdersService.recoverSession()` (§5.6.7): una sola recuperación en vuelo. */
  private recoverSession(): void {
    if (this.recovery && !this.recovery.closed) return;
    this.recovery = this.auth.me().subscribe();
  }
}

/** Primer texto no vacío de cada campo de `{error: {campo: [texto, …]}}`; un `error` que no es objeto da `{}`. */
function firstMessages(errors: unknown): Record<string, string> {
  if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) return {};
  const fields: Record<string, string> = {};
  for (const [field, value] of Object.entries(errors)) {
    const first: unknown = Array.isArray(value) ? value[0] : value;
    if (typeof first === 'string' && first.trim() !== '') fields[field] = first;
  }
  return fields;
}
